import React, { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import * as _ from 'lodash-es'
import { Field, formValueSelector } from 'redux-form/immutable'
import styled, { useTheme } from 'styled-components/native'

import { overrideCompanyNames } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { isImmutable } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/immutableUtils'
import { isLeidosUser } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/userUtils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { diagnoses } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/endOfSession/endOfSessionConstants'
import { UserInfo } from '@lyrahealth-inc/shared-app-logic/src/models/client/Client'
import { ClientListClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/ClientListClient'
import { CustomerInfo } from '@lyrahealth-inc/shared-app-logic/src/models/customer/Customer'
import {
  Address,
  ProviderAdminProviderInfo,
  ProviderRates,
} from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import { ProviderUser } from '@lyrahealth-inc/shared-app-logic/src/models/user/User'
import PrimaryButton from '@lyrahealth-inc/ui-core/lib/atoms/buttons/primaryButton/PrimaryButton'
import DateInputField from '@lyrahealth-inc/ui-core/lib/atoms/form/dateInputField/DateInputField'
import MaskedInputField from '@lyrahealth-inc/ui-core/lib/atoms/form/maskedInputField/MaskedInputField'
import RadioGroup from '@lyrahealth-inc/ui-core/lib/atoms/form/radioGroup/RadioGroup'
import Tooltip from '@lyrahealth-inc/ui-core/lib/atoms/tooltip/Tooltip'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { CheckboxRFF } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/Checkbox'
import { InputFieldRFF } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/InputField'
import { SelectFieldRFF } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/SelectField'
import { TypeAheadFieldAdapter } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/typeAhead/TypeAheadFieldAdapter'
import { ExclamationCircleIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ExclamationCircleIcon'
import { ExclamationIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ExclamationIcon'
import { InfoIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/InfoIcon'
import { TOOLTIP_TRIGGER_ICON, TooltipMaxWidth } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tooltip/Tooltip'
import { BaseModal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/baseModal/BaseModal'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import styles from './paymentRequestForm.module.scss'
import { getSessionLengthTypeMismatchWarning, SESSION_LENGTH_TYPE_MISMATCH_WARNING } from './paymentRequestValidate'
import { getRequestPaymentCustomerSessionTypes } from '../../../../data/requestPayment/requestPaymentSelectors'
import { useClientsData } from '../../../../lyraTherapy/clients/data/hooks/useClientsData'
import BlockPaymentCard from '../../../../requestPayment/blockPayment/BlockPaymentCard'
import { getCustomerSessionTypes } from '../../../../requestPayment/data/requestPaymentActions'
import {
  attendanceOptions,
  companyMessages,
  customerExclusionList,
  ebtTypes,
  getConditions,
  guaranteedTimeSessionTypes,
  LEIDOS_CHAT_MODALITY,
  modalityOptions,
  PAYMENT_FORM,
  ROLES,
  sessionTypesDisplay,
  severityOptions,
} from '../../../constants/appConstants'
import { hasRole, isECDCProvider, trimInitialWhiteSpace } from '../../../utils/utils'
import { useCompaniesWithWarnings } from '../../hooks/useCompaniesWithWarnings'
import LyraCodeExplanationModalContent from '../../lyraCode/LyraCodeExplanationModalContent'
import HiddenField from '../HiddenField'
import PaymentAdditionalInfoContent from '../paymentInfo/PaymentAdditionalInfoContent'

import type { RootState } from '../../../../data/store'

// Format function to convert Immutable values to native arrays for TypeAheadFieldAdapter
const formatImmutableToArray = <T,>(value: T | T[] | { toJS: () => T[] } | undefined | null): T[] | undefined => {
  if (!value) return undefined
  if (isImmutable({ value })) {
    return (value as { toJS: () => T[] }).toJS()
  }
  return value as T[]
}

const EngageMatchMessage = styled.View({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
})

const IconWrapper = styled.View({
  marginTop: 6,
  minWidth: 20,
})

const MessageText = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginLeft: theme.spacing['8px'],
  flex: 1,
}))

const initialEvaluationSessionTypes = [
  'individual_initial_evaluation',
  'couples_initial_evaluation',
  'family_initial_evaluation',
  'group_initial_evaluation',
  'child_initial_evaluation',
]

const paymentFormSelector = formValueSelector(PAYMENT_FORM.NAME)

type SelectOption = { value: string; label: string }

const PaymentRequestForm: React.FC<PaymentRequestFormProps> = ({
  attendance,
  disabledFields = {},
  handleSubmit,
  submitting,
  submitFunction,
  primaryButtonText,
  providers,
  showEligibleMemberDetails,
  sessionType,
  showDiagnosisDescription,
  clientOutcomesEligibility,
  providerChangeHandler,
  showOutcomesMessage,
  patientOutcomesAgreed,
  initialCompany,
  initialDiagnoses,
  lastSessionHandler,
  sponsoringCompanies,
  guaranteedTime,
  guaranteedTimeEligible,
  selectedCompany,
  lyraCodeValidationUser,
  disableSubmitButton,
  showLyraCode = false,
  hideDependentField = false,
  selectedProvider,
  loggedInUser,
  config,
  dateInputKey,
  providerAddresses,
  locationChangeHandler,
  primaryCondition,
  secondaryCondition,
  ebtType,
  rates,
}) => {
  const { isECDCEnabled, isDAProviderMigrated, isCaregiverTrainingEnabled } = useFlags()
  const companiesWithWarnings = useCompaniesWithWarnings()
  const [modalContent, setModalContent] = useState<JSX.Element | null>(null)
  const [addresses, setAddresses] = useState<Address[]>([])
  const [companyMessage, setCompanyMessage] = useState<React.ReactNode | null>(null)
  const [sessionDropDownOptions, setSessionDropDownOptions] = useState<SelectOption[]>([])
  const [displayedModalityOptions, setDisplayedModalityOptions] = useState<SelectOption[]>([])
  const conditionsList = useMemo(() => getConditions({}), [])
  const { data: activeClients = [] } = useClientsData({}, { skip: !isDAProviderMigrated })
  const firstName = useSelector((state: RootState) => paymentFormSelector(state, PAYMENT_FORM.FIELDS.FIRST_NAME))
  const lastName = useSelector((state: RootState) => paymentFormSelector(state, PAYMENT_FORM.FIELDS.LAST_NAME))
  const currentModality = useSelector((state: RootState) => paymentFormSelector(state, PAYMENT_FORM.FIELDS.MODALITY))
  const sessionDuration = useSelector((state: RootState) =>
    paymentFormSelector(state, PAYMENT_FORM.FIELDS.SESSION_DURATION),
  )
  const hasSessionLengthTypeMismatchWarning = Boolean(getSessionLengthTypeMismatchWarning(sessionType, sessionDuration))

  const { colors } = useTheme()

  const helpCenterLink = (
    <a href='https://provider-support.lyrahealth.com/hc/en-us/requests/new?ticket_form_id=538748'>Help Center</a>
  )
  const providerOptions: SelectOption[] =
    providers.length > 0
      ? providers.map((provider) => ({ value: provider.lyra_id, label: provider.full_name ?? '' }))
      : [{ value: loggedInUser.id, label: `${loggedInUser.first_name} ${loggedInUser.last_name}` }]
  const sponsoringCompanyLink = (
    <a
      href='https://provider-support.lyrahealth.com/hc/en-us/categories/4402698962707-Sponsoring-Company-Session-Limits-and-Policies'
      target='_blank'
      rel='noreferrer'
    >
      here
    </a>
  )

  const nuclearBadgedLink: Partial<Record<string, string>> = {
    nextera: 'https://provider-support.lyrahealth.com/hc/en-us/articles/4413021102099-NextEra-Energy',
    aps: 'https://provider-support.lyrahealth.com/hc/en-us/articles/7926599714451',
  }

  const companies = useMemo(
    () =>
      sponsoringCompanies
        ?.filter((company: CustomerInfo) => !customerExclusionList.includes(company.value))
        .map((company) => {
          const newCompany = _.cloneDeep(company)
          if (newCompany.value in overrideCompanyNames) {
            newCompany.label = overrideCompanyNames[newCompany.value as keyof typeof overrideCompanyNames]
          }
          return newCompany
        }),
    [sponsoringCompanies],
  )

  const customerAliases = useMemo(() => {
    const aliases: CustomerInfo[] = []
    companies.forEach((company) => {
      company.customer_aliases?.forEach((customerAlias: string) => {
        aliases.push({ ...company, label: customerAlias })
      })
    })
    return aliases
  }, [companies])

  const engageClientMatchMessage = useMemo(() => {
    if (!isDAProviderMigrated) {
      return null
    }

    if (!firstName || !lastName) {
      return null
    }

    const matched = Array.isArray(activeClients)
      ? activeClients.some(
          (c: ClientListClientObject) =>
            c.first_name.trim().toLowerCase() === firstName.trim().toLowerCase() &&
            c.last_name.trim().toLowerCase() === lastName.trim().toLowerCase(),
        )
      : false

    if (matched) {
      return (
        <EngageMatchMessage>
          <IconWrapper>
            <ExclamationCircleIcon fillColor={styles.x_highlight} />
          </IconWrapper>
          <MessageText>
            <BodyText
              testID={tID('PaymentRequestForm-engageClientMatchMessage')}
              text={`${firstName} appears to be a new Engage client. To submit the invoicing flow, access your client's session on your calendar.`}
            />
          </MessageText>
        </EngageMatchMessage>
      )
    }

    return null
  }, [firstName, lastName, activeClients, isDAProviderMigrated])

  const dispatch = useDispatch()
  const customerName = selectedCompany?.[0]?.value
  const isLeidos = isLeidosUser(customerName)
  const customerSessionTypes = useSelector((state) => getRequestPaymentCustomerSessionTypes(state, customerName))
  useEffect(() => {
    if (customerName && !customerSessionTypes) {
      dispatch(getCustomerSessionTypes(customerName))
    }
  }, [dispatch, customerName, customerSessionTypes])

  useEffect(() => {
    // Session types for selected company
    let selectedSessionTypes: { value: string; label: string }[] = sessionTypesDisplay

    if (!config.allSessionTypes && customerSessionTypes && customerSessionTypes.length > 0) {
      // if data exists from v1/customers/session-types API response, use it
      selectedSessionTypes = customerSessionTypes
    }

    if (loggedInUser.bcba) {
      // BCBA providers only see caregiver training session if the feature flag is on
      selectedSessionTypes = isCaregiverTrainingEnabled
        ? selectedSessionTypes.filter(
            (element: { value: string; label: string }) => element.value === 'caregiver_training_for_neuro_session',
          )
        : []
    } else {
      if (
        !config.allSessionTypes &&
        rates &&
        !loggedInUser.onsite &&
        !(isLeidos && loggedInUser.access_type === 'T0')
      ) {
        // Filter out session types the chosen provider does not have rates set for,
        // as long as the logged-in user is not a payment admin, an onsite provider, a BCBA provider, or a Leidos T0 provider.
        selectedSessionTypes = selectedSessionTypes.filter(
          (element: { value: string; label: string }) =>
            Object.prototype.hasOwnProperty.call(rates, element.value) || element.value.toLowerCase() == 'other',
        )
      }

      // Filter out caregiver training session if the user is not a payment admin or the feature flag is off
      if (!config.allSessionTypes || !isCaregiverTrainingEnabled) {
        selectedSessionTypes = selectedSessionTypes.filter(
          (element: { value: string; label: string }) => element.value !== 'caregiver_training_for_neuro_session',
        )
      }

      // Hide legacy medication management follow-up types if all complexity-based types are available
      if (rates) {
        const hasAdultComplexityRates =
          Object.prototype.hasOwnProperty.call(rates, 'medication_management_follow_up_low_complexity') &&
          Object.prototype.hasOwnProperty.call(rates, 'medication_management_follow_up_moderate_complexity') &&
          Object.prototype.hasOwnProperty.call(rates, 'medication_management_follow_up_high_complexity')
        const hasMinorComplexityRates =
          Object.prototype.hasOwnProperty.call(rates, 'minor_medication_management_follow_up_low_complexity') &&
          Object.prototype.hasOwnProperty.call(rates, 'minor_medication_management_follow_up_moderate_complexity') &&
          Object.prototype.hasOwnProperty.call(rates, 'minor_medication_management_follow_up_high_complexity')

        selectedSessionTypes = selectedSessionTypes.filter((element: { value: string; label: string }) => {
          if (hasAdultComplexityRates && element.value === 'medication_management_follow_up_session') return false
          if (hasMinorComplexityRates && element.value === 'minor_medication_management_follow_up_evaluation')
            return false
          return true
        })
      }
    }

    setSessionDropDownOptions(selectedSessionTypes)
  }, [
    config,
    loggedInUser.onsite,
    loggedInUser.bcba,
    loggedInUser.access_type,
    isLeidos,
    rates,
    selectedCompany,
    customerSessionTypes,
    isCaregiverTrainingEnabled,
  ])

  useEffect(() => {
    const modalityEntries = Object.entries(isLeidos ? { ...modalityOptions, ...LEIDOS_CHAT_MODALITY } : modalityOptions)
    if (providerAddresses && providerAddresses.length > 0) {
      setAddresses(providerAddresses)
      setDisplayedModalityOptions(
        modalityEntries.map(([value, label]) =>
          value === 'in_person' && addresses && addresses.length === 1
            ? { value, label: `${label} - ${addresses[0].street1}` }
            : { value, label },
        ),
      )
    } else {
      setDisplayedModalityOptions(modalityEntries.map(([value, label]) => ({ value, label })))
    }
  }, [addresses, providerAddresses, isLeidos])

  const onHandleSubmit = () => {
    if (submitting) {
      handleSubmit(submitFunction)
    }
  }

  const getSponsoringCompanyMessage = (companyName: string) => {
    return companiesWithWarnings[companyName]?.map((warning: string) => {
      switch (warning) {
        case companyMessages.MULTIPLE_INSTANCES:
          return (
            <p key={warning} className={styles['company-message']}>
              <ExclamationCircleIcon fillColor={styles.x_highlight} />
              <p className={styles['message-text']} data-test-id='PaymentRequestForm-multipleInstancesMessage'>
                Make sure you&apos;ve selected the right company name. Some companies have multiple accounts to cater to
                different patient needs. Ask your patient if you&apos;re not sure which company account they belong to.
              </p>
            </p>
          )
        case companyMessages.NUCLEAR_BADGED:
          return (
            <p key={warning} className={styles['company-message']}>
              <ExclamationCircleIcon fillColor={styles.x_highlight} />
              <p className={styles['message-text']} data-test-id='PaymentRequestForm-nuclearBadgedMessage'>
                Ask your patient if they are nuclear badged. There are special considerations to take for nuclear badged
                patients. <a href={nuclearBadgedLink[companyName]}>View nuclear access help center article</a>
              </p>
            </p>
          )
        default:
          return null
      }
    })
  }
  return (
    <form
      className={styles.container}
      onSubmit={handleSubmit(submitFunction) as React.FormEventHandler<HTMLFormElement>}
    >
      <Field name={PAYMENT_FORM.FIELDS.PRACTICE_ID} component={HiddenField} />
      <h2 data-test-id='PaymentRequestForm-title'>Patient Details</h2>
      {providers.length == 0 && hasRole(loggedInUser.roles, ROLES.PAYMENTS_ADMIN) ? (
        <Field name={PAYMENT_FORM.FIELDS.PROVIDER_LYRA_ID} component={HiddenField} />
      ) : (
        <div className={`row ${styles['select-provider']}`}>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name={PAYMENT_FORM.FIELDS.PROVIDER_LYRA_ID}
              onChange={(...args: unknown[]) => {
                // the placeholder option is selectable on web; ignore an empty pick
                if (args[1]) providerChangeHandler(...args)
              }}
              component={SelectFieldRFF}
              label='Rendering Provider *'
              subLabel={`If the correct rendering provider is not listed in the dropdown, please contact [providers@lyrahealth.com](mailto:providers@lyrahealth.com) prior to submitting the payment request.`}
              options={providerOptions}
              placeholder='Select'
              useStringValue
            />
          </div>
        </div>
      )}
      {!isLeidos && guaranteedTimeEligible ? (
        <>
          <Field
            name={PAYMENT_FORM.FIELDS.IS_GUARANTEED_TIME}
            component={CheckboxRFF}
            title="Submit as 'Unused Session Slot-No client'"
          />

          {guaranteedTime && (
            <div className='row'>
              <div className='col-xs-12 col-sm-6'>
                <Field
                  name={PAYMENT_FORM.FIELDS.GUARANTEED_TIME_SESSION_TYPE}
                  component={SelectFieldRFF}
                  label='Unused session type'
                  options={guaranteedTimeSessionTypes}
                  placeholder='Select'
                />
              </div>
            </div>
          )}
        </>
      ) : (
        []
      )}
      <div className={styles['request-payment-block-card']}>
        <div className='row'>
          <BlockPaymentCard loggedInUser={loggedInUser} selectedProvider={selectedProvider} />
          <div data-test-id='PaymentRequestForm-companiesTypeAhead'>
            <div className='col-xs-12 col-sm-6'>
              <Field
                name={PAYMENT_FORM.FIELDS.COMPANY}
                defaultSelected={initialCompany}
                component={TypeAheadFieldAdapter}
                format={formatImmutableToArray}
                options={[...companies, ...customerAliases]}
                multiSelect={false}
                disabled={disabledFields.company}
                label='Sponsoring Company *'
                placeholder='Sponsoring Company'
                emptyLabel='No results found'
                shouldForceTouch
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  // TypeAheadFieldAdapter calls onChange with the selected options, not an event
                  const [selectedCompany] = e as unknown as { value?: string }[]
                  // check if company requires a special message to be displayed for DA providers only
                  if (selectedCompany?.value) {
                    setCompanyMessage(getSponsoringCompanyMessage(selectedCompany.value))
                  }
                }}
                toolTipContent={
                  <div>
                    Select the company from which the client is receiving benefits. If the client is a dependent, please
                    make sure that they are providing you with the sponsoring company of the eligible employee. For
                    information on our current sponsoring companies and their session limits please click{' '}
                    {sponsoringCompanyLink}.
                  </div>
                }
                toolTipTriggerConfig={{ icon: TOOLTIP_TRIGGER_ICON.INFO, size: 16 }}
                toolTipSize={TooltipMaxWidth.WIDE}
              />
            </div>
            {companyMessage && <div className={styles['company-message-container']}>{companyMessage}</div>}
          </div>
        </div>

        {showLyraCode ? (
          <div className='row'>
            <div className='col-xs-12 col-sm-6'>
              <Field
                name={PAYMENT_FORM.FIELDS.LYRA_CODE}
                type='text'
                autocomplete='off'
                component={MaskedInputField}
                mask='###-###-###'
                placeholder='888-888-888'
                label={`Lyra code ${_.get(selectedCompany, '[0].lyra_code_required') === 'true' ? '*' : ''}`}
                indicateAsyncValidity
                asyncValid={!_.isEmpty(lyraCodeValidationUser)}
                hintContent={
                  !isLeidos && (
                    <span
                      onClick={() =>
                        setModalContent(<LyraCodeExplanationModalContent closeModal={() => setModalContent(null)} />)
                      }
                      onKeyDown={() => {}}
                      className={styles['lyra-code-explanation']}
                      role='button'
                      tabIndex={0}
                    >
                      Where can I find this?
                    </span>
                  )
                }
              />
            </div>
          </div>
        ) : null}

        {!isLeidos && !hideDependentField && (
          <div className='row'>
            <div className='col-xs-12 col-sm-6'>
              <Field
                name={PAYMENT_FORM.FIELDS.RELATIONSHIP}
                label='Is the patient a dependent *'
                component={RadioGroup}
                styleType='stroked'
                isStacked={false}
                disabled={disabledFields.attendance}
                buttons={[
                  { text: 'Yes', value: 'dependent' },
                  { text: 'No', value: 'employee' },
                ]}
              />
            </div>
          </div>
        )}

        {!isLeidos && (
          <div className='row'>
            <div className='col-xs-12 col-sm-6'>
              <Field
                name={PAYMENT_FORM.FIELDS.DATE_OF_BIRTH}
                type='tel'
                component={MaskedInputField}
                mask='11/11/1111'
                placeholder='MM/DD/YYYY'
                label='Patient Date of Birth *'
                disabled={disabledFields.date_of_birth}
              />
            </div>
          </div>
        )}

        <div className='row'>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name={PAYMENT_FORM.FIELDS.FIRST_NAME}
              component={InputFieldRFF}
              label='Patient First Name *'
              placeholder='First Name'
              readOnly={disabledFields.first_name}
              normalize={trimInitialWhiteSpace}
              toolTipContent={
                <div>
                  The name on the invoice is the legal name under which the client is receiving benefits rather than a
                  nickname. Please note that your client may have registered with Lyra using a different name from the
                  one you were given.
                </div>
              }
              toolTipTriggerConfig={{ icon: TOOLTIP_TRIGGER_ICON.INFO }}
              toolTipHoverEnabled={true}
            />
          </div>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name={PAYMENT_FORM.FIELDS.LAST_NAME}
              component={InputFieldRFF}
              label='Patient Last Name *'
              placeholder='Last Name'
              readOnly={disabledFields.last_name}
              normalize={trimInitialWhiteSpace}
            />
          </div>
        </div>

        {!isLeidos && engageClientMatchMessage && engageClientMatchMessage}

        {!isLeidos && showEligibleMemberDetails ? (
          <div className={styles.section}>
            <h2>Eligible Member Details</h2>
            <div className='row'>
              <div className='col-xs-12 col-sm-6'>
                <Field
                  name={PAYMENT_FORM.FIELDS.ELIGIBLE_MEMBER_FIRST_NAME}
                  component={InputFieldRFF}
                  label="Member's First Name *"
                  placeholder='First Name'
                  normalize={trimInitialWhiteSpace}
                />
              </div>
              <div className='col-xs-12 col-sm-6'>
                <Field
                  name={PAYMENT_FORM.FIELDS.ELIGIBLE_MEMBER_LAST_NAME}
                  component={InputFieldRFF}
                  label="Member's Last Name *"
                  placeholder='Last Name'
                  normalize={trimInitialWhiteSpace}
                />
              </div>
            </div>
            <div className='row'>
              <div className='col-xs-12 col-sm-6'>
                <Field
                  name={PAYMENT_FORM.FIELDS.ELIGIBLE_MEMBER_DOB}
                  type='tel'
                  component={MaskedInputField}
                  mask='11/11/1111'
                  placeholder='MM/DD/YYYY'
                  label="Member's Date of Birth *"
                />
              </div>
            </div>
          </div>
        ) : (
          []
        )}

        <div className={styles.section}>
          <h2>Session Details</h2>
          <div className='row'>
            <div className='col-xs-12 col-sm-6'>
              <Field
                name={PAYMENT_FORM.FIELDS.VISIT_DATE}
                type='tel'
                key={dateInputKey}
                component={DateInputField}
                showMonthDropdown
                showYearDropdown
                dropdownMode='select'
                placeholder='MM/DD/YYYY'
                label='Visit Date *'
                shouldForceTouch
                mask='11/11/1111'
              />
            </div>
            <div className='col-xs-12 col-sm-6'>
              <Tooltip
                content='Please choose a sponsoring company'
                show={!selectedCompany || !selectedCompany.length}
                delayHide={0}
              >
                <Field
                  name={PAYMENT_FORM.FIELDS.SESSION_TYPE}
                  label='Session Type *'
                  component={SelectFieldRFF}
                  options={sessionDropDownOptions}
                  useStringValue
                  disabled={!selectedCompany || !selectedCompany.length || disabledFields.session_type}
                  onUserChange={(sessionType: string) => {
                    if (!isLeidos && initialEvaluationSessionTypes.includes(sessionType)) {
                      setModalContent(
                        <PaymentAdditionalInfoContent
                          closeModal={() => setModalContent(null)}
                          title={'Confirm client’s eligibility before submitting payment'}
                          description={
                            'You can confirm your client’s eligibility and benefits using the Eligibility tab.'
                          }
                          url={'/eligibility-checker'}
                        />,
                      )
                    }
                  }}
                  toolTipContent={
                    <div>
                      Select the type of session that took place (individual initial evaluation, individual follow up,
                      couple’s follow up, etc.). Once you’ve selected a session type from the drop down list, your
                      session rate will appear under “session rate” for the session types that you are contracted for.
                      If you are having trouble selecting a session type, make sure that you have clicked on the
                      sponsoring company from the drop down menu under “Patient Details,” rather than typing it in.
                    </div>
                  }
                  toolTipTriggerConfig={{ icon: TOOLTIP_TRIGGER_ICON.INFO, size: 16 }}
                  toolTipSize={TooltipMaxWidth.WIDE}
                />
              </Tooltip>
            </div>
          </div>

          {!isLeidos && sessionType === 'other' ? (
            <div className='row'>
              <div className='col-xs-12'>
                <Field
                  name={PAYMENT_FORM.FIELDS.OTHER_SESSION_DESCRIPTION}
                  component={InputFieldRFF}
                  label='Session Type Description *'
                  placeholder='Session Description'
                  sublabel='Since you selected "Other", please describe the session'
                />
              </div>
            </div>
          ) : (
            []
          )}

          <div className='row'>
            <div
              className={`col-xs-12 col-sm-6 ${
                hasSessionLengthTypeMismatchWarning ? styles['session-length-warning'] : ''
              }`}
            >
              <Field
                name={PAYMENT_FORM.FIELDS.SESSION_DURATION}
                component={InputFieldRFF}
                label='Session Length (Minutes) *'
                placeholder='Number of minutes'
                toolTipContent={
                  <div>
                    Indicate the length of the session using a numerical value such as “50” for a 50-minute session.
                  </div>
                }
                toolTipTriggerConfig={{ icon: TOOLTIP_TRIGGER_ICON.INFO }}
                toolTipHoverEnabled={true}
              />
              {hasSessionLengthTypeMismatchWarning && (
                <div className={styles['session-length-warning-message']} role='status' aria-live='polite'>
                  <ExclamationIcon fillColor='currentColor' size={16} />
                  <span>{SESSION_LENGTH_TYPE_MISMATCH_WARNING}</span>
                </div>
              )}
            </div>
          </div>

          <div className='row'>
            <div className='col-xs-12 col-sm-6'>
              <Field
                name={PAYMENT_FORM.FIELDS.MODALITY}
                label='Modality *'
                subLabel={`For compliance and billing accuracy, please select the correct session modality. Intentional misrepresentation of billing information is considered billing fraud, which may impact your contract with Lyra and/or your license.

Practice locations we have on file are listed below. For changes, complete an [Address Update Form](https://provider-support.lyrahealth.com/hc/en-us/requests/new?ticket_form_id=360000124287).`}
                component={SelectFieldRFF}
                options={displayedModalityOptions}
                disabled={disabledFields.modality}
                onUserChange={(modality: string) => {
                  if (modality === 'video' || modality === 'tele_therapy' || modality === 'live_messaging') {
                    locationChangeHandler('')
                  } else {
                    if (addresses?.length === 1) {
                      locationChangeHandler(addresses[0])
                    }
                  }
                }}
              />
            </div>
            {!guaranteedTime && !isLeidos && currentModality === 'in_person' && addresses?.length > 1 && (
              <div className={`col-xs-12 col-sm-6 ${styles['in-person-location']}`}>
                <Field
                  name={PAYMENT_FORM.FIELDS.LOCATION}
                  label='Location *'
                  placeholder='In Person Address'
                  labelKey='street1'
                  component={TypeAheadFieldAdapter}
                  format={formatImmutableToArray}
                  options={addresses}
                  multiSelect={false}
                  disabled={disabledFields.location}
                ></Field>
              </div>
            )}
          </div>
          <div className='row'>
            <div className='col-xs-12 col-sm-6'>
              <Tooltip
                content='Please choose a sponsoring company'
                show={!selectedCompany || !selectedCompany.length}
                delayHide={0}
              >
                <Field
                  name={PAYMENT_FORM.FIELDS.SESSION_RATE}
                  readOnly={sessionType !== 'other'}
                  component={InputFieldRFF}
                  label='Session Rate *'
                  placeholder='Session Rate'
                  toolTipContent={
                    <div>
                      The session rate will automatically appear once a session type is selected and only for your
                      contracted session types. If you do not see your rate appear after clicking on the appropriate
                      session type, please reference your LOA to ensure you are contracted for that type of treatment.
                      If you confirm that you are contracted for that session type, please reach out to
                      providers@lyrahealth.com as we may need to activate your account.
                    </div>
                  }
                  toolTipTriggerConfig={{ icon: TOOLTIP_TRIGGER_ICON.INFO }}
                  toolTipHoverEnabled={true}
                  toolTipSize={TooltipMaxWidth.WIDE}
                />
              </Tooltip>
            </div>
          </div>
          <div className='row'>
            <div className='col-sm-12'>
              <Field
                name={PAYMENT_FORM.FIELDS.ATTENDANCE}
                component={RadioGroup}
                styleType='stroked'
                label='Patient Attendance'
                buttons={[
                  { text: attendanceOptions.attended, value: 'attended' },
                  { text: attendanceOptions.last_minute_cancellation, value: 'last_minute_cancellation' },
                  { text: attendanceOptions.no_show, value: 'no_show' },
                ]}
                disabled={disabledFields.attendance}
              />
            </div>
          </div>
          {!isLeidos && (
            <div className='row'>
              {attendance?.toLocaleLowerCase() === attendanceOptions.attended.toLocaleLowerCase() &&
                ((!isECDCProvider(loggedInUser) && !hasRole(loggedInUser.roles, ROLES.PAYMENTS_ADMIN)) ||
                  !isECDCEnabled) && (
                  <div className='col-sm-12'>
                    <Field
                      name={PAYMENT_FORM.FIELDS.DIAGNOSIS}
                      component={TypeAheadFieldAdapter}
                      format={formatImmutableToArray}
                      defaultSelected={initialDiagnoses}
                      options={diagnoses}
                      placeholder='Select primary need(s)'
                      label='Primary Need(s) *'
                      disabled={disabledFields.diagnosis}
                      toolTipContent="Use your best judgment for selecting your client's primary clinical need(s). You can update this information in future invoices. Choose all that apply."
                      toolTipTriggerConfig={{ icon: TOOLTIP_TRIGGER_ICON.INFO, size: 16 }}
                    />
                  </div>
                )}
              {showDiagnosisDescription ? (
                <div className='row'>
                  <div className='col-xs-12'>
                    <Field
                      name={PAYMENT_FORM.FIELDS.OTHER_DIAGNOSIS}
                      component={InputFieldRFF}
                      label='Diagnosis Description *'
                      placeholder='Diagnosis Description'
                      sublabel='Since you selected "Other", please describe the diagnosis'
                      readOnly={guaranteedTime}
                    />
                  </div>
                </div>
              ) : (
                []
              )}
              {attendance?.toLocaleLowerCase() === attendanceOptions.attended.toLocaleLowerCase() &&
                isECDCEnabled &&
                (isECDCProvider(loggedInUser) || hasRole(loggedInUser.roles, ROLES.PAYMENTS_ADMIN)) && (
                  <>
                    <div className='col-sm-12'>
                      <div className={styles['sub-label']}>
                        List primary and secondary clinical targets for this episode with ratings of severity and the
                        evidence-based psychotherapy used in sessions, as permitted by applicable law.
                      </div>
                      <br></br>
                    </div>
                    <div className='col-sm-12'>
                      <Field
                        name={PAYMENT_FORM.FIELDS.PRIMARY_CONDITION}
                        component={TypeAheadFieldAdapter}
                        format={formatImmutableToArray}
                        multiSelect={false}
                        options={conditionsList}
                        placeholder='Select primary condition'
                        label='Indicate the primary condition that you are targeting in therapy: *'
                      >
                        <option hidden value=''>
                          Select
                        </option>
                        {conditionsList.map((element: { value: string; label: string }, i) => {
                          return (
                            <option key={i} value={element.value}>
                              {element.label}
                            </option>
                          )
                        })}
                      </Field>
                      {primaryCondition && primaryCondition.length > 0 && primaryCondition[0].value === 'other' ? (
                        <div className='row'>
                          <div className='col-xs-12'>
                            <Field
                              name={PAYMENT_FORM.FIELDS.OTHER_PRIMARY_DESCRIPTION}
                              component={InputFieldRFF}
                              label='Condition Type Description *'
                              placeholder='Condition description'
                              sublabel='Since you selected "Other", please describe the condition'
                            />
                          </div>
                        </div>
                      ) : (
                        []
                      )}
                      <Field
                        name={PAYMENT_FORM.FIELDS.PRIMARY_CONDITION_SEVERITY}
                        component={RadioGroup}
                        styleType='stroked'
                        label="For this client's primary condition, indicate the severity:"
                        buttons={[
                          { text: severityOptions.mild, value: 'mild' },
                          { text: severityOptions.moderate, value: 'moderate' },
                          { text: severityOptions.severe, value: 'severe' },
                        ]}
                      />
                      <Field
                        name={PAYMENT_FORM.FIELDS.SECONDARY_CONDITION}
                        component={TypeAheadFieldAdapter}
                        format={formatImmutableToArray}
                        options={conditionsList}
                        placeholder='Select all secondary conditions'
                        label='Indicate any secondary, co-occurring conditions (Select all that apply):'
                      />
                      {secondaryCondition &&
                      secondaryCondition.some((item: { label: string; value: string }) => item.value === 'other') ? (
                        <div className='row'>
                          <div className='col-xs-12'>
                            <Field
                              name={PAYMENT_FORM.FIELDS.OTHER_SECONDARY_DESCRIPTION}
                              component={InputFieldRFF}
                              label='Secondary Condition Description'
                              placeholder='Secondary Condition description'
                              sublabel='Since you selected "Other", please describe the condition'
                            />
                          </div>
                        </div>
                      ) : (
                        []
                      )}
                      <Field
                        name={PAYMENT_FORM.FIELDS.EBT_TYPE}
                        component={TypeAheadFieldAdapter}
                        format={formatImmutableToArray}
                        multiSelect={false}
                        options={ebtTypes}
                        placeholder='Select primary EBT'
                        label='What was the primary Evidence Based Therapy (EBT) that you used for this session?'
                      >
                        <option hidden value=''>
                          Select
                        </option>
                        {ebtTypes.map((element: { value: string; label: string }, i) => {
                          return (
                            <option key={i} value={element.value}>
                              {element.label}
                            </option>
                          )
                        })}
                      </Field>
                      {ebtType && ebtType.length > 0 && ebtType[0].value === 'other' ? (
                        <div className='row'>
                          <div className='col-xs-12'>
                            <Field
                              name={PAYMENT_FORM.FIELDS.OTHER_EBT_DESCRIPTION}
                              component={InputFieldRFF}
                              label='EBT Type Description'
                              placeholder='EBT description'
                              sublabel='Since you selected "Other", please describe the EBT used'
                            />
                          </div>
                        </div>
                      ) : (
                        []
                      )}
                    </div>
                  </>
                )}
            </div>
          )}

          <div className='row'>
            <div className='col-xs-12 col-sm-6'>
              <h2 className={styles['sub-header']}>
                Discontinue Care
                <Tooltip
                  delayHide={0}
                  content={
                    <div>
                      <div>
                        Treatment is considered{' '}
                        <BodyText style={{ fontWeight: 'bold' }} text='completed' color={colors.textButton} /> if you
                        and your patient decide that they have achieved their goals and do not need further treatment at
                        this time.
                      </div>
                      <br />
                      <div>
                        Treatment is considered{' '}
                        <BodyText style={{ fontWeight: 'bold' }} text='terminated' color={colors.textButton} /> if
                        treatment has to end for reasons including, but not limited to, patient relocation, change in
                        patient’s employment status, patient stops responding, etc.
                      </div>
                      <br />
                      <div> The patient is welcome to return for care at any time.</div>
                    </div>
                  }
                >
                  <InfoIcon size={16} />
                </Tooltip>
              </h2>
              <div className={styles['discontinue-checkboxes']}>
                <Field
                  name={PAYMENT_FORM.FIELDS.LAST_SESSION}
                  onChange={(...args: unknown[]) =>
                    lastSessionHandler(PAYMENT_FORM.FIELDS.LAST_SESSION, args[1] as boolean)
                  }
                  component={CheckboxRFF}
                  readOnly={disabledFields.last_session}
                  title='Last session - treatment completed'
                />
              </div>
              <div className={styles['discontinue-checkboxes']}>
                <Field
                  name={PAYMENT_FORM.FIELDS.LAST_SESSION_TERMINATED}
                  onChange={(...args: unknown[]) =>
                    lastSessionHandler(PAYMENT_FORM.FIELDS.LAST_SESSION_TERMINATED, args[1] as boolean)
                  }
                  component={CheckboxRFF}
                  readOnly={disabledFields.last_session_terminated}
                  title='Last session - treatment terminated'
                />
              </div>
            </div>
          </div>
        </div>

        {!isLeidos && (
          <div className={`row ${styles['comments-container']}`}>
            <div className='col-xs-12'>
              <Field
                name={PAYMENT_FORM.FIELDS.NOTES}
                component={InputFieldRFF}
                placeholder='Add a comment'
                label='Clinical Notations'
                multiline={true}
                numberOfLines={3}
                sublabel={
                  'For any urgent clinical concerns please reach out [here](https://provider-support.lyrahealth.com/hc/en-us/requests/new)'
                }
                toolTipContent={
                  <div className={styles['tooltip-text']}>
                    If there are additional notes you’d like to include, please type them here. If you have a question,
                    please submit a request through the {helpCenterLink}.
                  </div>
                }
                toolTipTriggerConfig={{ icon: TOOLTIP_TRIGGER_ICON.INFO }}
                toolTipHoverEnabled={true}
              />
            </div>
          </div>
        )}

        <div className='row'>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name={PAYMENT_FORM.FIELDS.REFERENCE_NUMBER}
              component={InputFieldRFF}
              label='Reference Number'
              placeholder='Reference Number'
              toolTipContent={
                <div className={styles['tooltip-text']}>
                  This is an optional field which allows you to track payments if you use an internal invoicing system.
                  If you leave this blank, the system will generate a reference number for the invoice after it has been
                  approved.
                </div>
              }
              toolTipTriggerConfig={{ icon: TOOLTIP_TRIGGER_ICON.INFO }}
              toolTipHoverEnabled={true}
            />
          </div>
        </div>

        {!isLeidos && showOutcomesMessage ? (
          <div className={styles['outcomes-message']}>
            <h2 className={styles['outcomes-header']}>Client Outcomes</h2>
            {sessionType ? (
              patientOutcomesAgreed ? (
                <span>
                  {clientOutcomesEligibility ? (
                    <span>
                      <p>
                        We will collect Outcomes data from this client once a month via email. As soon as the client
                        submits responses, we’ll notify you.
                        <br />
                        <a
                          href='https://provider-support.lyrahealth.com/hc/en-us/articles/115012608808-Automated-Outcomes-Collection-Process-and-FAQ'
                          target='_blank'
                          rel='noreferrer'
                        >
                          Learn more
                        </a>
                      </p>
                    </span>
                  ) : (
                    <p>
                      We will not collect Outcomes data from this client. We currently only collect Outcomes data from
                      clients who are 18 years of age or older and who participate in individual therapy, couples
                      therapy, or medication management.
                      <br />
                      <a
                        href='https://provider-support.lyrahealth.com/hc/en-us/articles/360000740168'
                        target='_blank'
                        rel='noreferrer'
                      >
                        Learn more
                      </a>
                    </p>
                  )}
                </span>
              ) : (
                <p>
                  This client has chosen not to submit Outcomes to Lyra.
                  <br />
                  <a
                    href='https://provider-support.lyrahealth.com/hc/en-us/articles/360000740168'
                    target='_blank'
                    rel='noreferrer'
                  >
                    Learn more
                  </a>
                </p>
              )
            ) : (
              <p>Please select a Session Type above.</p>
            )}
          </div>
        ) : (
          []
        )}

        <div className={`row ${styles['submit-container']}`}>
          <div className='col-xs-12'>
            {primaryButtonText === 'Next' && <p>We need some additional information</p>}
            <PrimaryButton
              id='test-requestpayment-submit'
              type='submit'
              disabled={disableSubmitButton}
              isLoading={submitting}
              onKeyDown={onHandleSubmit()}
            >
              {primaryButtonText}
            </PrimaryButton>
          </div>
        </div>
      </div>
      <BaseModal body={modalContent ?? <div />} isOpen={!!modalContent} closeModal={() => setModalContent(null)} />
    </form>
  )
}

type PaymentRequestFormProps = {
  primaryButtonText: string
  providers: ProviderAdminProviderInfo[]
  handleSubmit: (...args: unknown[]) => unknown
  submitting: boolean
  submitFunction: (...args: unknown[]) => unknown
  showEligibleMemberDetails: boolean
  sessionType: string
  dateInputKey: string
  showDiagnosisDescription: boolean
  providerChangeHandler: (...args: unknown[]) => void
  showOutcomesMessage: boolean
  patientOutcomesAgreed: boolean
  clientOutcomesEligibility: boolean
  initialCompany: CustomerInfo[]
  initialDiagnoses: { label: string; value: string }[]
  lastSessionHandler: (fieldName: string, value: boolean) => void
  sponsoringCompanies: CustomerInfo[]
  guaranteedTimeEligible: boolean
  guaranteedTime: boolean
  selectedCompany: CustomerInfo[]
  disableSubmitButton: boolean
  showLyraCode: boolean
  hideDependentField: boolean
  selectedProvider: ProviderAdminProviderInfo
  loggedInUser: ProviderUser
  config: any
  disabledFields: { [key: string]: any }
  lyraCodeValidationUser: UserInfo
  attendance: string
  providerAddresses: Address[]
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type -- called internally with string | Address but callers type as Address[]; changing call sites breaks form submission
  locationChangeHandler: Function
  primaryCondition: { label: string; value: string }[]
  secondaryCondition: { label: string; value: string }[]
  ebtType: { label: string; value: string }[]
  rates: ProviderRates
}

export default PaymentRequestForm
